// Quote Builder — Drilling & Handing block, shared.
//
// Lifted verbatim out of door-picker.ts (markup ~1237-1290, handlers ~509-570)
// so the SJB cabinet picker can embed the SAME component rather than a
// lookalike that drifts. door-picker.ts now renders through here too; its
// baseline harness (~/qa/qb-dom-harness/verify-door-picker-dom.ts) is what
// proves the move changed nothing.
//
// ── Two things the original relied on that had to become explicit ─────────
//
// 1. INSTANCING. The door picker has exactly one of these, so it queried
//    `[data-role='drill-mode']` across the whole picker. An SJB cabinet with
//    two doors needs two independently-configured blocks — left and right can
//    be mirrored, not symmetric — so every block carries a `data-drill-id`
//    and wiring is scoped to its own element.
//
// 2. PERSISTENCE. The original wrote straight into
//    `store.defaults.door.drilling` when the "Remember" toggle was on. That
//    is the door picker's policy, not the block's, so it is now a `persist`
//    callback the host supplies. The SJB picker passes none: a per-door hinge
//    side is a property of that door, not something to carry into the next
//    quote.

import { escapeHtml } from "./formatters";

export type DrillMode = "none" | "hinge";
export type DrillUnitType = "base" | "wall";
export type DrillSide = "L" | "R" | "T";

export interface DrillingSpec {
  mode: DrillMode;
  unitType: DrillUnitType;
  side: DrillSide;
  holeQty: number;
  topMm: number;
  bottomMm: number;
  /** qty − 2 middle distances, measured from the top. */
  midMm: number[];
}

/**
 * Which way the row of bores runs.
 *
 * A side-hung door pivots on a vertical edge, so its hinges sit one above
 * another and the distances are from the TOP and BOTTOM. A top-hung flap
 * pivots on its horizontal top edge, so its hinges sit side by side and the
 * distances are from the LEFT and RIGHT ends. Same two numbers, different
 * axis, and nothing about the stored spec says which — so every label, every
 * position and every sentence of prose goes through this.
 */
export type DrillAxis = "vertical" | "horizontal";

export function drillAxis(spec: Pick<DrillingSpec, "side">): DrillAxis {
  return spec.side === "T" ? "horizontal" : "vertical";
}

/**
 * The words for each end of the run, and for what a middle hole is measured
 * from.
 *
 * FIELD NAMES ARE DELIBERATELY NOT RENAMED. `topMm` / `bottomMm` hold the
 * left and right distances on a top-hung flap, which reads oddly — but the
 * spec is PERSISTED (the door picker writes its defaults to
 * store.defaults.door.drilling) and is shared by three pickers, so renaming
 * would either break stored quotes or need a migration for a cosmetic gain.
 * The names mean "start of the run" and "end of the run"; this function is
 * the only thing allowed to turn them into English.
 */
export function drillAxisLabels(axis: DrillAxis): {
  start: string; end: string; startCap: string; endCap: string; midFrom: string;
} {
  return axis === "horizontal"
    ? { start: "left", end: "right", startCap: "Left", endCap: "Right", midFrom: "left" }
    : { start: "top", end: "bottom", startCap: "Top", endCap: "Bottom", midFrom: "top" };
}

export function blankDrillingSpec(over: Partial<DrillingSpec> = {}): DrillingSpec {
  return {
    mode: "none", unitType: "base", side: "L",
    holeQty: 2, topMm: 98, bottomMm: 98, midMm: [],
    ...over,
  };
}

/**
 * Resolved hole positions, in mm from the START of the run — the top of a
 * side-hung door, the left end of a top-hung flap. Mirrors the door picker's
 * own `[top, ...mids, h - bottom]` (door-picker.ts:842).
 *
 * The span has to come from the caller because it differs by axis: a
 * vertical run is measured across the HEIGHT, a horizontal one across the
 * WIDTH. Passing a bare number keeps every existing caller working and means
 * "the height".
 *
 * A bare number WITH side "T" returns [] rather than a guess. Measuring a
 * top-hung flap's bores down its height is precisely the bug this replaces,
 * and it is a confident wrong answer — which on a fabrication instruction is
 * worse than no answer. An empty list is visible; a plausible wrong one is not.
 */
export function drillingPositions(
  spec: DrillingSpec,
  leaf: number | { height: number; width: number },
): number[] {
  if (spec.mode !== "hinge") return [];
  const horizontal = drillAxis(spec) === "horizontal";
  const span = typeof leaf === "number"
    ? (horizontal ? null : leaf)
    : (horizontal ? leaf.width : leaf.height);
  if (span == null || !(span > 0)) return [];
  return [spec.topMm, ...(spec.midMm ?? []).filter((n) => Number.isFinite(n)),
          span - spec.bottomMm];
}

/**
 * How a hole pattern reads in words: "3 holes — top 98mm · middle 713mm ·
 * bottom 98mm".
 *
 * TOP AND BOTTOM ARE SEPARATE STORED VALUES, and this prints them as such.
 * The SJB picker used to render the resolved positions instead — "3 holes at
 * 98, 713, 1327mm from top" — which took the bottom hole's distance from the
 * WRONG END. Both distances are independently editable in the block above
 * (a door can be top 98 / bottom 120), so a reader cannot recover the bottom
 * distance from a from-the-top figure without also knowing the door height,
 * and nothing on the quote line makes that obvious.
 *
 * The middle holes genuinely ARE measured from the top, which is how the block
 * labels them, so they are printed as-is: "middle" at three holes, "hole N" at
 * four, matching the input labels a retailer just typed into.
 *
 * Shared so the LH summary, the RH line description and the RH note cannot
 * drift — they described the same holes three different ways before.
 */
export function holePositionText(
  spec: Pick<DrillingSpec, "topMm" | "bottomMm" | "midMm" | "holeQty">
      & Partial<Pick<DrillingSpec, "side">>,
): string {
  const mids = (spec.midMm ?? []).filter((n) => Number.isFinite(n));
  const qty = spec.holeQty || mids.length + 2;
  // `side` is OPTIONAL, and absent means vertical. That is safe rather than a
  // guess: a left- and a right-hung door both read top/bottom, so the only
  // value that changes the wording is "T", and a caller that has a side at
  // all passes it. The 2026-09-07 fix made these words agree with the input
  // labels; this makes both of them follow the axis, which is what the
  // agreement was supposed to be about.
  const ax = drillAxisLabels(drillAxis({ side: spec.side ?? "L" }));
  const parts = [`${ax.start} ${spec.topMm}mm`];
  mids.forEach((m, i) => parts.push(
    qty === 3 ? `middle ${m}mm` : `hole ${i + 2} ${m}mm`));
  parts.push(`${ax.end} ${spec.bottomMm}mm`);
  // Only on a HORIZONTAL run. Vertical wording is Steve's, signed off
   // 2026-09-07 as "3 holes — top 98mm · middle 713mm · bottom 98mm", and a
  // middle measured from the top is the obvious reading there. Left-to-right
  // is not obvious, so that case says which end it is from; the approved
  // wording is left byte-identical.
  const from = (mids.length && ax.midFrom !== "top") ? ` (middles from the ${ax.midFrom})` : "";
  return `${qty} hole${qty === 1 ? "" : "s"} — ${parts.join(" · ")}${from}`;
}

export interface DrillingBlockOpts {
  /** Distinguishes instances. Every data-role below is scoped by it. */
  id: string;
  /** Heading; the SJB picker names the door ("Door 1 — 715 × 596mm"). */
  title?: string;
  /** Extra line under the heading, e.g. where the pre-fill came from. */
  note?: string;
}

export function renderDrillingBlock(s: DrillingSpec, opts: DrillingBlockOpts): string {
  const id = escapeHtml(opts.id);
  // Resolved once so the hint, the labels and the data attribute cannot
  // disagree about which axis is being described.
  const axis = drillAxis(s);
  const ax = { axis, l: drillAxisLabels(axis) };
  return `
    <div class="qb-door-drill" data-role="drill-block" data-drill-id="${id}">
      <div class="qb-door-drill__head">
        <span class="qb-door-drill__title">${escapeHtml(opts.title ?? "Drilling & Handing")}</span>
        <div class="qb-door-toggle-row" data-role="drill-mode-row">
          <button type="button" class="${s.mode === "none" ? "is-active" : ""}" data-role="drill-mode" data-drill-mode="none">None</button>
          <button type="button" class="${s.mode === "hinge" ? "is-active" : ""}" data-role="drill-mode" data-drill-mode="hinge">35mm Hinge</button>
        </div>
      </div>
      ${opts.note ? `<p class="qb-door-drill__note">${escapeHtml(opts.note)}</p>` : ""}
      ${s.mode === "hinge" ? `
        <div class="qb-door-drill__rows">
          <div>
            <label class="qb-door-label">Unit type</label>
            <div class="qb-door-toggle-row">
              <button type="button" class="${s.unitType === "base" ? "is-active" : ""}" data-role="unit-type" data-unit-type="base">Base</button>
              <button type="button" class="${s.unitType === "wall" ? "is-active" : ""}" data-role="unit-type" data-unit-type="wall">Wall</button>
            </div>
          </div>
          <div>
            <label class="qb-door-label">Handing</label>
            <div class="qb-door-toggle-row">
              <button type="button" class="${s.side === "L" ? "is-active" : ""}" data-role="handing" data-handing="L">Left</button>
              <button type="button" class="${s.side === "R" ? "is-active" : ""}" data-role="handing" data-handing="R">Right</button>
              <button type="button" class="${s.side === "T" ? "is-active" : ""}" data-role="handing" data-handing="T">Top</button>
            </div>
          </div>
          <div>
            <label class="qb-door-label">Qty of holes</label>
            <select class="qb-door-input qb-door-input--narrow" data-role="hole-qty">
              ${[2, 3, 4, 5].map((n) => `<option value="${n}"${s.holeQty === n ? " selected" : ""}>${n} holes</option>`).join("")}
            </select>
          </div>
          ${ax.axis === "horizontal"
            ? `<p class="qb-door-drill__note" data-role="drill-axis-note">
                 Top-hung: the hinges run along the top edge, so these distances are
                 measured from the <strong>left</strong> and <strong>right</strong> ends,
                 across the width.
               </p>`
            : ""}
          <div class="qb-door-drill__dist" data-drill-axis="${ax.axis}">
            <div>
              <label class="qb-door-label">${ax.l.startCap} distance (mm)</label>
              <input class="qb-door-input" type="number" min="0" step="1" value="${s.topMm}" data-role="top-mm" />
            </div>
            ${Array.from({ length: Math.max(0, s.holeQty - 2) }, (_, i) => {
              const midLabel = s.holeQty === 3
                ? "Middle distance (mm)"
                : `Hole ${i + 2} from ${ax.l.midFrom} (mm)`;
              const v = s.midMm[i] ?? "";
              return `<div>
                <label class="qb-door-label">${midLabel}</label>
                <input class="qb-door-input" type="number" min="0" step="1" value="${v}" data-role="mid-mm" data-mid-idx="${i}" />
              </div>`;
            }).join("")}
            <div>
              <label class="qb-door-label">${ax.l.endCap} distance (mm)</label>
              <input class="qb-door-input" type="number" min="0" step="1" value="${s.bottomMm}" data-role="bottom-mm" />
            </div>
          </div>
        </div>
      ` : ""}
    </div>
  `;
}

export interface DrillingWireOpts {
  /** Called after any change. The host repaints. */
  onChange(): void;
  /** Host policy for the "Remember" defaults. Omit for no persistence. */
  persist?: (patch: Partial<Pick<DrillingSpec, "mode" | "unitType" | "side" | "topMm" | "bottomMm">>) => void;
}

/**
 * Bind one block. `block` is the element renderDrillingBlock produced — NOT
 * the whole picker — so several can coexist without their controls colliding.
 */
export function wireDrillingBlock(block: HTMLElement, spec: DrillingSpec, opts: DrillingWireOpts): void {
  const $$ = (sel: string): HTMLElement[] => Array.from(block.querySelectorAll<HTMLElement>(sel));
  const $ = <T extends Element>(sel: string): T | null => block.querySelector<T>(sel);

  $$("[data-role='drill-mode']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const v = btn.dataset["drillMode"] as DrillMode;
      spec.mode = v;
      opts.persist?.({ mode: v });
      opts.onChange();
    });
  });
  $$("[data-role='unit-type']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const v = btn.dataset["unitType"] as DrillUnitType;
      spec.unitType = v;
      opts.persist?.({ unitType: v });
      opts.onChange();
    });
  });
  $$("[data-role='handing']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const v = btn.dataset["handing"] as DrillSide;
      spec.side = v;
      opts.persist?.({ side: v });
      opts.onChange();
    });
  });
  $<HTMLSelectElement>("[data-role='hole-qty']")?.addEventListener("change", (e) => {
    spec.holeQty = Number((e.target as HTMLSelectElement).value) || 2;
    // Middle distances are qty-dependent; a stale array would mislabel them.
    spec.midMm = [];
    opts.onChange();
  });
  $<HTMLInputElement>("[data-role='top-mm']")?.addEventListener("input", (e) => {
    const n = Number((e.target as HTMLInputElement).value) || 0;
    spec.topMm = n;
    opts.persist?.({ topMm: n });
  });
  $<HTMLInputElement>("[data-role='bottom-mm']")?.addEventListener("input", (e) => {
    const n = Number((e.target as HTMLInputElement).value) || 0;
    spec.bottomMm = n;
    opts.persist?.({ bottomMm: n });
  });
  $$("[data-role='mid-mm']").forEach((el) => {
    el.addEventListener("input", () => {
      const i = Number((el as HTMLInputElement).dataset["midIdx"]);
      if (!Number.isFinite(i)) return;
      if (!spec.midMm) spec.midMm = [];
      spec.midMm[i] = Number((el as HTMLInputElement).value) || 0;
    });
  });
}
